/**
 * @todo Remove this instance or separate similar functionality.
 * @description This is a custom fetch client for Corev2 API.
 * @param url - The URL to fetch.
 * @param options - The options to fetch.
 * @returns The response from the fetch.
 */

import { getAppConfig, resolveAbsoluteUrl } from "@/app/config/app-config"

export type BodyType<BodyData> = BodyData

export type ErrorType<Error> = Error

const getBody = async <T>(response: Response | Request): Promise<T> => {
	const contentType = response.headers.get("content-type") ?? ""

	if (contentType.includes("application/json")) {
		return response.json() as Promise<T>
	}

	if (
		contentType.includes("application/pdf") ||
		contentType.includes("application/octet-stream")
	) {
		return response.blob() as Promise<T>
	}

	return response.text() as Promise<T>
}

const getUrl = (contextUrl: string): string => {
	const baseUrl = resolveAbsoluteUrl(getAppConfig().oauthUrl)

	const requestURL = new URL(contextUrl, baseUrl)

	return requestURL.href
}

const getHeaders = (
	headers?: HeadersInit,
	body?: BodyInit | null,
): HeadersInit => {
	const result = new Headers(headers)

	const isFormData = typeof FormData !== "undefined" && body instanceof FormData
	const isBlob = typeof Blob !== "undefined" && body instanceof Blob
	if (!isFormData && !isBlob && !result.has("Content-Type")) {
		result.set("Content-Type", "application/json")
	}

	return result
}

/**
 * Custom fetch client for Corev2 API.
 * Accepts (url, options) as standard fetch.
 */
export const customFetch = async <T>(
	url: string,
	options: RequestInit,
): Promise<T> => {
	try {
		const requestUrl = getUrl(url)

		const headers = getHeaders(options.headers, options.body ?? null)

		const requestInit: RequestInit = {
			...options,
			headers,
			credentials: "include",
		}

		const request = new Request(requestUrl, requestInit)

		const response = await fetch(request)

		// HTTP errors
		if (!response.ok) {
			let data: unknown
			try {
				data = await getBody(response)
			} catch {
				data = null
			}
			const errorData = {
				data,
				status: response.status,
				message: response.statusText,
			}
			throw errorData
		}

		const data = await getBody<T>(response)

		return { status: response.status, data, headers: response.headers } as T
	} catch (error) {
		// Suppress logs for AbortError
		const isAbortError =
			typeof error === "object" &&
			error !== null &&
			"name" in error &&
			error.name === "AbortError"
		if (!isAbortError) {
			console.error("Error fetching data:", error)
		}
		throw error
	}
}

export default customFetch
